import type { Editor } from '@tiptap/react';
import {
    ArrowUturnLeftIcon,
    ArrowUturnRightIcon,
    ListBulletIcon,
    NumberedListIcon,
    PhotoIcon
} from '@heroicons/react/24/outline';
import type { RichEditorPreset } from '../types';
import { LinkPopoverButton } from './LinkPopoverButton';
import { PresetMenu } from './PresetMenu';
import { CalloutToolbarGroup, LayoutToolbarGroup, TextStylingToolbarGroup } from './ToolbarExtras';

interface ToolbarButtonProps {
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
}

/**
 * Single icon-or-text toolbar button. Used by Toolbar and by the popover
 * triggers (Link, Presets). The active state renders a darker border +
 * gray-100 background to mirror the tiptap mark/active state convention.
 */
export function ToolbarButton({ onClick, active, disabled, title, children }: ToolbarButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            aria-pressed={active}
            className={
                'inline-flex h-7 min-w-[28px] items-center justify-center rounded-md border px-1.5 text-xs font-medium transition-colors ' +
                (active
                    ? 'border-gray-700 bg-gray-100 text-gray-900'
                    : 'border-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900') +
                ' disabled:opacity-40 disabled:cursor-not-allowed'
            }
        >
            {children}
        </button>
    );
}

interface ToolbarProps {
    editor: Editor;
    presets?: RichEditorPreset[];
    onApplyPreset?: (preset: RichEditorPreset) => void;
    /**
     * Extra content pinned to the far right of the bar (e.g. a character
     * count). Uses its own `ml-auto` wrapper, so when `presets` is also
     * passed (which self-applies `ml-auto` via PresetMenu) the two share the
     * row's free space rather than both landing flush right - no current
     * caller combines them, so that's untested, not just unhandled.
     */
    rightSlot?: React.ReactNode;
    /**
     * False hides the image control, for fields whose storage has nowhere to
     * put an uploaded file (see RichEditorProps.allowImages).
     */
    allowImages?: boolean;
    /** Shows the callout/notice-box control. Off by default: the block is
     *  always in the schema, but only announcement-style surfaces offer it. */
    allowCallouts?: boolean;
    /** Shows the divider and spacer controls. Off by default, same reasoning. */
    allowLayoutBlocks?: boolean;
    /** Shows the alignment and highlight controls. Off by default, same reasoning. */
    allowTextStyling?: boolean;
}

/**
 * Top-of-editor formatting bar. Buttons are grouped (history | inline
 * marks | headings | lists | inserts) with small vertical separators.
 * Presets dropdown is right-aligned via `ml-auto` on PresetMenu so it
 * sits opposite the inline tools.
 */
export function Toolbar({
    editor,
    presets,
    onApplyPreset,
    rightSlot,
    allowImages = true,
    allowCallouts = false,
    allowLayoutBlocks = false,
    allowTextStyling = false
}: ToolbarProps) {
    return (
        <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 px-2 py-1 bg-gray-50/40">
            {/* History */}
            <ToolbarButton
                title="Undo (⌘Z)"
                disabled={!editor.can().undo()}
                onClick={() => editor.chain().focus().undo().run()}
            >
                <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
            </ToolbarButton>
            <ToolbarButton
                title="Redo (⌘⇧Z)"
                disabled={!editor.can().redo()}
                onClick={() => editor.chain().focus().redo().run()}
            >
                <ArrowUturnRightIcon className="h-3.5 w-3.5" />
            </ToolbarButton>
            <div className="mx-1 h-4 w-px bg-gray-300" />

            {/* Inline marks */}
            <ToolbarButton
                title="Bold"
                active={editor.isActive('bold')}
                onClick={() => editor.chain().focus().toggleBold().run()}
            >
                <span className="font-bold">B</span>
            </ToolbarButton>
            <ToolbarButton
                title="Italic"
                active={editor.isActive('italic')}
                onClick={() => editor.chain().focus().toggleItalic().run()}
            >
                <span className="italic">I</span>
            </ToolbarButton>
            <ToolbarButton
                title="Underline"
                active={editor.isActive('underline')}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
                <span className="underline">U</span>
            </ToolbarButton>
            <div className="mx-1 h-4 w-px bg-gray-300" />

            {/* Headings */}
            <ToolbarButton
                title="Heading 1"
                active={editor.isActive('heading', { level: 1 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            >
                H1
            </ToolbarButton>
            <ToolbarButton
                title="Heading 2"
                active={editor.isActive('heading', { level: 2 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            >
                H2
            </ToolbarButton>
            <ToolbarButton
                title="Heading 3"
                active={editor.isActive('heading', { level: 3 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            >
                H3
            </ToolbarButton>
            <div className="mx-1 h-4 w-px bg-gray-300" />

            {/* Lists */}
            <ToolbarButton
                title="Bullet list"
                active={editor.isActive('bulletList')}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
                <ListBulletIcon className="h-3.5 w-3.5" />
            </ToolbarButton>
            <ToolbarButton
                title="Numbered list"
                active={editor.isActive('orderedList')}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
                <NumberedListIcon className="h-3.5 w-3.5" />
            </ToolbarButton>
            <div className="mx-1 h-4 w-px bg-gray-300" />

            {/* Optional blocks - each group is opt-in per surface */}
            {allowTextStyling && <TextStylingToolbarGroup editor={editor} />}
            {allowCallouts && <CalloutToolbarGroup editor={editor} />}
            {allowLayoutBlocks && <LayoutToolbarGroup editor={editor} />}

            {/* Inserts */}
            {allowImages && (
                <ToolbarButton
                    title="Insert images"
                    onClick={() => editor.commands.insertImageGrid()}
                >
                    <PhotoIcon className="h-3.5 w-3.5" />
                </ToolbarButton>
            )}
            <LinkPopoverButton editor={editor} />

            {presets && presets.length > 0 && onApplyPreset && (
                <PresetMenu presets={presets} onPick={onApplyPreset} />
            )}
            {rightSlot && <div className="ml-auto flex items-center gap-2">{rightSlot}</div>}
        </div>
    );
}
